/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import {
  useRef,
  useState,
  useMemo,
  useEffect,
  createContext,
  useContext,
  FC,
  PropsWithChildren,
  Dispatch,
  SetStateAction,
} from 'react';
import { isDutchLocale } from '../utils/helpers';
import { useIsDashboardPage, useMinAum } from '../hooks';
import {
  useUserExchangeProviderDataQuery,
  ExchangeCode,
  SelectedExchangeDocument,
  WizardStatusType,
  SelectedExchangeQuery,
  UserExchangeProviderDataQuery,
} from '../apollo';
import { useModals } from './Modals';

const Exchange: FC<PropsWithChildren> = ({ children }) => {
  const initialModalWasShown = useRef(false);
  const dashboardPage = useIsDashboardPage();
  const { dispatch } = useModals();
  const {
    client,
    data: { getUser: user, selectedExchange, getUserExchanges: exchanges } = {},
  } = useUserExchangeProviderDataQuery();

  const [exchangeCode, setExchangeCode] = useState<ExchangeCode>(() =>
    getInitialExchangeCode(exchanges, selectedExchange)
  );

  const exchange = useMemo(
    () =>
      (exchangeCode &&
        exchanges?.find(({ exchangeCode: code }) => code === exchangeCode)) ??
      null,
    [exchangeCode, exchanges]
  );

  const exchangeWizardActive = useMemo(
    () => !exchange || !isWizardComplete(exchange.wizardStatus),
    [exchange]
  );

  const exchangeWizardVisible = useMemo(
    () => dashboardPage && exchangeWizardActive,
    [dashboardPage, exchangeWizardActive]
  );

  // Save change of exchange to cache
  useEffect(() => {
    client.writeQuery<SelectedExchangeQuery>({
      query: SelectedExchangeDocument,
      data: { selectedExchange: exchange?.exchangeCode || null },
    });
  }, [exchange, client]);

  useMinAum(exchange, exchangeWizardActive);

  // Show initial portfolio modal if no exchange has been selected
  useEffect(() => {
    if (!dashboardPage) return;
    if (user?.isAdmin || !user?.profileValid) return;
    if (initialModalWasShown.current) return;
    if (
      !exchanges?.every(
        ({ wizardStatus }) => wizardStatus === WizardStatusType.NoKeys
      )
    ) {
      return;
    }

    initialModalWasShown.current = true;

    dispatch({
      type: 'setModalContent',
      payload: {
        name: 'initialPortfolioModal',
      },
    });
  }, [dashboardPage, user?.isAdmin, user?.profileValid, exchanges, dispatch]);

  const contextValue = useMemo<ContextState>(
    () => ({
      exchange,
      exchangeWizardActive,
      exchangeWizardVisible,
      setExchange: setExchangeCode,
    }),
    [exchangeWizardActive, exchangeWizardVisible, exchange]
  );

  return (
    <ExchangeContext.Provider value={contextValue}>
      {children}
    </ExchangeContext.Provider>
  );
};

type ExchangeType = UserExchangeProviderDataQuery['getUserExchanges'][number];

type ContextState = {
  exchange: Nullish<ExchangeType>;
  exchangeWizardActive: boolean;
  exchangeWizardVisible: boolean;
  setExchange: Dispatch<SetStateAction<ExchangeCode>>;
};

const isWizardComplete = (status: WizardStatusType) =>
  status === WizardStatusType.Disabled;

const getInitialExchangeCode = (
  exchanges: UserExchangeProviderDataQuery['getUserExchanges'] | undefined,
  selectedExchange: ExchangeCode | null | undefined
): ExchangeCode => {
  if (!exchanges) return getDefaultExchange();

  const existingSelectedExchange =
    selectedExchange &&
    exchanges.some(({ exchangeCode }) => exchangeCode === selectedExchange)
      ? selectedExchange
      : null;

  const completedExchanges = exchanges.filter(({ wizardStatus }) =>
    isWizardComplete(wizardStatus)
  );

  if (completedExchanges.length === exchanges.length) {
    return existingSelectedExchange || getDefaultExchange();
  }

  return (
    completedExchanges[0]?.exchangeCode ||
    existingSelectedExchange ||
    getDefaultExchange()
  );
};

const getDefaultExchange = () =>
  isDutchLocale() ? ExchangeCode.Bitvavo : ExchangeCode.Kraken;

const ExchangeContext = createContext<ContextState>({
  exchange: null,
  exchangeWizardActive: true,
  exchangeWizardVisible: false,
  setExchange: () => undefined,
});

export const useExchange = () => useContext(ExchangeContext);

export { Exchange };
